/**
 * Test #9 — ONE mapper from any assistant failure to what the user is told.
 *
 * The backend answers with stable, machine-readable bodies
 * ({ ok:false, error:<code>, message:<text>, retryAfterSeconds? }) for the
 * cases a user can act on; this module recognises those codes and shows the
 * server's own message. The generic "unavailable" text is reserved for truly
 * unknown failures (network down, a 5xx without a body) — never for a
 * configuration problem.
 */
import { ApiError, parseApiErrorBody } from '@/api/v2Client';

export type AssistantErrorKind =
  | 'not-configured'
  | 'key-invalid'
  | 'rate-limited'
  | 'unavailable'
  | 'disabled'
  | 'unknown';

export interface AssistantErrorDescription {
  readonly kind: AssistantErrorKind;
  /** The server's code when it sent one (e.g. 'AssistantKeyInvalid'). */
  readonly code: string | null;
  readonly message: string;
  /** Only for 'rate-limited': seconds to wait before Send is allowed again. */
  readonly retryAfterSeconds: number | null;
}

export const GENERIC_ASSISTANT_MESSAGE =
  'The assistant is unavailable right now. Your data is unaffected.';

/**
 * The status endpoint returns only a reason; these mirror the server's
 * messages for the banner. Error BODIES always carry the server text itself.
 */
export const ASSISTANT_CONFIG_MESSAGES: Readonly<Record<'AssistantNotConfigured' | 'AssistantKeyInvalid', string>> = {
  AssistantNotConfigured:
    "The AI assistant isn't configured: no API key is set on the server. Ask an administrator to set it.",
  AssistantKeyInvalid:
    "The AI assistant's API key was rejected by the provider. Ask an administrator to check it.",
};

const KIND_BY_CODE: Readonly<Record<string, AssistantErrorKind>> = {
  AssistantNotConfigured: 'not-configured',
  AssistantKeyInvalid: 'key-invalid',
  AssistantRateLimited: 'rate-limited',
  AssistantUnavailable: 'unavailable',
  'assistant-disabled': 'disabled',
  // The pre-Test #9 SSE frame code — still a known, honest "unavailable".
  'assistant-unavailable': 'unavailable',
};

const DEFAULT_RETRY_SECONDS = 60;

export function isConfigurationError(d: AssistantErrorDescription): boolean {
  return d.kind === 'not-configured' || d.kind === 'key-invalid';
}

/** A thrown error from createConversation / ask (HTTP status + JSON body). */
export function describeAssistantError(err: unknown): AssistantErrorDescription {
  if (err instanceof ApiError) {
    const body = parseApiErrorBody(err);
    const code = typeof body?.error === 'string' ? body.error : null;
    const kind = code !== null ? KIND_BY_CODE[code] : undefined;
    const serverMessage =
      typeof body?.message === 'string' && body.message.trim() !== '' ? body.message : null;
    if (kind !== undefined) {
      const retry =
        kind === 'rate-limited'
          ? typeof body?.retryAfterSeconds === 'number' && body.retryAfterSeconds > 0
            ? Math.ceil(body.retryAfterSeconds)
            : DEFAULT_RETRY_SECONDS
          : null;
      return { kind, code, message: serverMessage ?? GENERIC_ASSISTANT_MESSAGE, retryAfterSeconds: retry };
    }
    if (err.status === 429) {
      // The app-wide limiter (not assistant-specific) — still a rate limit.
      const retry =
        typeof body?.retryAfterSeconds === 'number' && body.retryAfterSeconds > 0
          ? Math.ceil(body.retryAfterSeconds)
          : DEFAULT_RETRY_SECONDS;
      return {
        kind: 'rate-limited',
        code: code ?? 'AssistantRateLimited',
        message: `You're sending messages too quickly. Wait ${retry} seconds and try again.`,
        retryAfterSeconds: retry,
      };
    }
  }
  return { kind: 'unknown', code: null, message: GENERIC_ASSISTANT_MESSAGE, retryAfterSeconds: null };
}

/** An SSE `error` frame ({ error, message }) after the stream started. */
export function describeAssistantStreamError(evt: {
  error?: unknown;
  message?: unknown;
}): AssistantErrorDescription {
  const code = typeof evt.error === 'string' ? evt.error : null;
  const kind = code !== null ? (KIND_BY_CODE[code] ?? 'unknown') : 'unknown';
  const message = typeof evt.message === 'string' && evt.message.trim() !== '' ? evt.message : GENERIC_ASSISTANT_MESSAGE;
  return { kind, code, message, retryAfterSeconds: null };
}

/** The banner / notice text for a status snapshot; null when configured. */
export function assistantStatusMessage(status: { configured: boolean; reason?: 'AssistantNotConfigured' | 'AssistantKeyInvalid' }): string | null {
  if (status.configured) return null;
  return ASSISTANT_CONFIG_MESSAGES[status.reason ?? 'AssistantNotConfigured'];
}
